import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Dropdown, DropdownMenu, DropdownToggle, Nav, NavItem, NavLink, Row, TabContent, TabPane, Badge } from 'reactstrap';
import { Link } from 'react-router-dom';
import classnames from 'classnames';

import bell from "../../assets/images/svg/bell.svg";
import SimpleBar from "simplebar-react";

import InvoiceReviewModal from './InvoiceReviewModal';

import { 
  getNotifications, 
  setNotificationAsRead, 
  markAllAsRead 
} from "../../slices/notifications/thunk";
import { createSelector } from "reselect";

const selectNotificationState = (state) => state.Notification;

export const selectNotificationProperties = createSelector(
  selectNotificationState, 
  (notification) => ({
    notifications: notification.notifications,
    currentNotification: notification.currentNotification,
    unreadCount: notification.unreadCount,
    loading: notification.loading,
    error: notification.error,
    filters: notification.filters,
    unreadNotifications: notification.notifications.filter(n => !n.is_read),
    readNotifications: notification.notifications.filter(n => n.is_read),
  })
);

const NotificationDropdown = () => {
    const dispatch = useDispatch();
    const intervalRef = useRef(null);
    
    const {
        notifications,
        unreadCount,
        unreadNotifications,
        readNotifications,
        loading,
        error,
    } = useSelector(selectNotificationProperties);

    const [isNotificationDropdown, setIsNotificationDropdown] = useState(false);
    const [isManualRefresh, setIsManualRefresh] = useState(false);
    
    const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
    const [selectedNotification, setSelectedNotification] = useState(null);
    
    const toggleNotificationDropdown = () => {
        setIsNotificationDropdown(!isNotificationDropdown);
    };

    const [activeTab, setActiveTab] = useState('1');
    const toggleTab = (tab) => {
        if (activeTab !== tab) {
            setActiveTab(tab);
        }
    };

    useEffect(() => {
        dispatch(getNotifications());

        intervalRef.current = setInterval(() => {
            dispatch(getNotifications());
        }, 30000);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [dispatch]);

    const handleManualRefresh = async () => {
        setIsManualRefresh(true);
        try {
            await dispatch(getNotifications()).unwrap();
        } catch (error) {
            console.error('Error refreshing notifications:', error);
        } finally {
            setTimeout(() => {
                setIsManualRefresh(false);
            }, 300);
        }
    };

    const handleMarkAsRead = async (notificationId, event) => {
        event.preventDefault();
        event.stopPropagation();
        
        try {
            await dispatch(setNotificationAsRead(notificationId)).unwrap();
            dispatch(getNotifications());
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    const handleMarkAllAsRead = async () => {
        try {
            await dispatch(markAllAsRead()).unwrap();
            dispatch(getNotifications());
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
        }
    };

    const handleNotificationClick = (notification, event) => {
        event.preventDefault();
        event.stopPropagation();
        
        if (notification.type === 'invoice_review') {
            setSelectedNotification(notification);
            setIsInvoiceModalOpen(true);
            setIsNotificationDropdown(false);
        }
    };

    const closeInvoiceModal = () => {
        setIsInvoiceModalOpen(false);
        setSelectedNotification(null);
    };

    const handleRefreshNotifications = () => {
        dispatch(getNotifications());
    };

    const handleInvoiceApprove = (notificationId) => {
        console.log('Invoice approved:', notificationId);
    };

    const handleInvoiceReject = (notificationId, reason) => {
        console.log('Invoice rejected:', notificationId, 'Reason:', reason);
    };

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'invoice_sent':
                return 'bx bx-receipt';
            case 'payment_received':
                return 'bx bx-money';
            case 'invoice_overdue':
                return 'bx bx-time-five';
            case 'contract_expired':
                return 'bx bx-file-blank';
            case 'invoice_review':
                return 'bx bx-task';
            default:
                return 'bx bx-bell';
        }
    };

    const getIconColor = (type) => {
        switch (type) {
            case 'invoice_sent':
                return 'bg-info-subtle text-info';
            case 'payment_received':
                return 'bg-success-subtle text-success';
            case 'invoice_overdue':
                return 'bg-danger-subtle text-danger';
            case 'contract_expired':
                return 'bg-warning-subtle text-warning';
            case 'invoice_review':
                return 'bg-primary-subtle text-primary';
            default:
                return 'bg-primary-subtle text-primary';
        }
    };

    const formatTimeAgo = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInMinutes = Math.floor((now - date) / (1000 * 60));
        
        if (diffInMinutes < 1) return 'Just now';
        if (diffInMinutes < 60) return `${diffInMinutes} min ago`;
        if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} hrs ago`;
        return `${Math.floor(diffInMinutes / 1440)} days ago`;
    };

    const renderNotificationItem = (notification, index) => (
        <div 
            key={notification.id}
            className={`text-reset notification-item d-block dropdown-item position-relative ${!notification.is_read ? 'active' : ''}`}
            onClick={notification.type === 'invoice_review' ? (e) => handleNotificationClick(notification, e) : undefined}
            style={{ cursor: notification.type === 'invoice_review' ? 'pointer' : 'default' }}
        >
            <div className="d-flex">
                <div className="avatar-xs me-3">
                    <span className={`avatar-title ${getIconColor(notification.type)} rounded-circle fs-16`}>
                        <i className={getNotificationIcon(notification.type)}></i>
                    </span>
                </div>
                <div className="flex-grow-1">
                    <div className="stretched-link">
                        <h6 className="mt-0 mb-2 lh-base">
                            {notification.title}
                            {notification.type === 'invoice_review' && (
                                <>
                                    {notification.is_approved === null || notification.is_approved === undefined ? (
                                        <Badge color="warning" className="ms-2 fs-11">
                                            Action Required
                                        </Badge>
                                    ) : (
                                        <Badge 
                                            color={notification.is_approved ? "success" : "danger"} 
                                            className="ms-2 fs-11"
                                        >
                                            {notification.is_approved ? "Approved" : "Rejected"}
                                        </Badge>
                                    )}
                                </>
                            )}
                        </h6>
                    </div>
                    <p className="mb-1 fs-13 text-muted">
                        {notification.message}
                    </p>
                    <p className="mb-0 fs-11 fw-medium text-uppercase text-muted">
                        <span><i className="mdi mdi-clock-outline"></i> {formatTimeAgo(notification.created_at)}</span>
                    </p>
                </div>
                <div className="px-2 fs-15">
                    <div className="form-check notification-check">
                        <input 
                            className="form-check-input" 
                            type="checkbox" 
                            value=""
                            checked={notification.is_read}
                            onChange={(e) => handleMarkAsRead(notification.id, e)}
                            id={`all-notification-check${index}`}
                        />
                        <label className="form-check-label" htmlFor={`all-notification-check${index}`}></label>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <React.Fragment>
            <Dropdown isOpen={isNotificationDropdown} toggle={toggleNotificationDropdown} className="topbar-head-dropdown ms-1 header-item">
                <DropdownToggle type="button" tag="button" className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle shadow-none">
                    <i className='bx bx-bell fs-22'></i>
                    {unreadCount > 0 && (
                        <span className="position-absolute topbar-badge fs-10 translate-middle badge rounded-pill bg-danger">
                            {unreadCount > 99 ? '99+' : unreadCount}
                            <span className="visually-hidden">unread messages</span>
                        </span>
                    )}
                </DropdownToggle>
                <DropdownMenu className="dropdown-menu-lg dropdown-menu-end p-0">
                    <div className="dropdown-head bg-primary bg-pattern rounded-top">
                        <div className="p-3">
                            <Row className="align-items-center">
                                <Col>
                                    <h6 className="m-0 fs-16 fw-semibold text-white"> Notifications </h6>
                                </Col>
                                <div className="col-auto dropdown-tabs d-flex align-items-center gap-2">
                                    <span className="badge bg-light-subtle text-body fs-13">
                                        {unreadCount} New
                                    </span>
                                    {/* Manual Refresh Button */}
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-light border-0 p-1"
                                        onClick={handleManualRefresh}
                                        disabled={isManualRefresh || loading}
                                        title="Refresh notifications"
                                    >
                                        <i className={`ri-refresh-line fs-16 ${(isManualRefresh || loading) ? 'fa-spin' : ''}`}></i>
                                    </button>
                                </div>
                            </Row>
                        </div>

                        <div className="px-2 pt-2">
                            <Nav className="nav-tabs dropdown-tabs nav-tabs-custom">
                                <NavItem>
                                    <NavLink
                                        href="#"
                                        className={classnames({ active: activeTab === '1' })}
                                        onClick={() => { toggleTab('1'); }}
                                    >
                                        All ({notifications?.length || 0})
                                    </NavLink>
                                </NavItem>
                                <NavItem>
                                    <NavLink
                                        href="#"
                                        className={classnames({ active: activeTab === '2' })}
                                        onClick={() => { toggleTab('2'); }}
                                    >
                                        Unread ({unreadCount || 0})
                                    </NavLink>
                                </NavItem>
                                <NavItem>
                                    <NavLink
                                        href="#"
                                        className={classnames({ active: activeTab === '3' })}
                                        onClick={() => { toggleTab('3'); }}
                                    >
                                        Read ({readNotifications?.length || 0})
                                    </NavLink>
                                </NavItem>
                            </Nav>
                        </div>
                    </div>

                    <TabContent activeTab={activeTab}>
                        {/* All Notifications Tab */}
                        <TabPane tabId="1" className="py-2 ps-2">
                            <SimpleBar style={{ maxHeight: "300px" }} className="pe-2">
                                {loading || isManualRefresh ? (
                                    <div className="text-center p-3">
                                        <div className="spinner-border spinner-border-sm" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p className="text-muted mb-0 mt-2 fs-12">
                                            {isManualRefresh ? 'Refreshing...' : 'Loading...'}
                                        </p>
                                    </div>
                                ) : !notifications || notifications.length === 0 ? (
                                    <div className="text-center p-3">
                                        <p className="text-muted mb-0">No notifications found</p>
                                    </div>
                                ) : (
                                    <>
                                        {notifications.map((notification, index) => 
                                            renderNotificationItem(notification, index)
                                        )}
                                    </>
                                )}

                                {notifications && notifications.length > 0 && !loading && !isManualRefresh && (
                                    <div className="my-3 text-center">
                                        <button 
                                            type="button" 
                                            className="btn btn-soft-primary waves-effect waves-light me-2"
                                            onClick={handleMarkAllAsRead}
                                            disabled={unreadCount === 0}
                                        >
                                            Mark All as Read
                                        </button>
                                    </div>
                                )}
                            </SimpleBar>
                        </TabPane>

                        {/* Unread Notifications Tab */}
                        <TabPane tabId="2" className="py-2 ps-2">
                            <SimpleBar style={{ maxHeight: "300px" }} className="pe-2">
                                {loading || isManualRefresh ? (
                                    <div className="text-center p-3">
                                        <div className="spinner-border spinner-border-sm" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p className="text-muted mb-0 mt-2 fs-12">
                                            {isManualRefresh ? 'Refreshing...' : 'Loading...'}
                                        </p>
                                    </div>
                                ) : !unreadNotifications || unreadNotifications.length === 0 ? (
                                    <div className="text-center p-3">
                                        <p className="text-muted mb-0">No unread notifications</p>
                                    </div>
                                ) : (
                                    <>
                                        {unreadNotifications.map((notification, index) => 
                                            renderNotificationItem(notification, index)
                                        )}
                                    </>
                                )}

                                {unreadNotifications && unreadNotifications.length > 0 && !loading && !isManualRefresh && (
                                    <div className="my-3 text-center">
                                        <button 
                                            type="button" 
                                            className="btn btn-soft-primary waves-effect waves-light"
                                            onClick={handleMarkAllAsRead}
                                        >
                                            Mark All as Read
                                        </button>
                                    </div>
                                )}
                            </SimpleBar>
                        </TabPane>

                        {/* Read Notifications Tab */}
                        <TabPane tabId="3" className="py-2 ps-2">
                            <SimpleBar style={{ maxHeight: "300px" }} className="pe-2">
                                {loading || isManualRefresh ? (
                                    <div className="text-center p-3">
                                        <div className="spinner-border spinner-border-sm" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <p className="text-muted mb-0 mt-2 fs-12">
                                            {isManualRefresh ? 'Refreshing...' : 'Loading...'}
                                        </p>
                                    </div>
                                ) : !readNotifications || readNotifications.length === 0 ? (
                                    <div className="w-25 w-sm-50 pt-3 mx-auto">
                                        <img src={bell} className="img-fluid" alt="user-pic" />
                                        <div className="text-center pb-5 mt-2">
                                            <h6 className="fs-18 fw-semibold lh-base">No read notifications yet</h6>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        {readNotifications.map((notification, index) => 
                                            renderNotificationItem(notification, index)
                                        )}
                                    </>
                                )}
                            </SimpleBar>
                        </TabPane>
                    </TabContent>
                </DropdownMenu>
            </Dropdown>

            {/* Invoice Review Modal */}
            <InvoiceReviewModal
                isOpen={isInvoiceModalOpen}
                toggle={closeInvoiceModal}
                notification={selectedNotification}
                onApprove={handleInvoiceApprove}
                onReject={handleInvoiceReject}
                onRefreshNotifications={handleRefreshNotifications}
            />
        </React.Fragment>
    );
};

export default NotificationDropdown;